import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { Mail, Phone, Plus, Trash2, MessageSquare, Send, Tag, X, GripVertical, CheckSquare } from 'lucide-react';

const StageOptions = ({ stages }) => stages?.map((s) => <option key={s.key} value={s.key}>{s.label}</option>);

const FIELD_TYPES = ['text', 'email', 'phone', 'number', 'textarea', 'select', 'checkbox', 'date'];

const Contacts = ({ lob }) => {
  const darkMode = useDarkMode();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [contacts, setContacts] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showTypes, setShowTypes] = useState(false);

  const load = useCallback(() => {
    if (!lob) return;
    setLoading(true); setErr('');
    api.get(`/contacts?lob_id=${lob.id}${search ? `&q=${encodeURIComponent(search)}` : ''}${stageFilter ? `&stage=${stageFilter}` : ''}`)
      .then((r) => setContacts(r.data))
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [lob?.id, search, stageFilter]);

  const loadTypes = useCallback(() => {
    if (!lob) return;
    api.get(`/contact-types?lob_id=${lob.id}`).then((r) => setTypes(r.data)).catch(() => {});
  }, [lob?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  const selected = contacts.find((c) => c.id === selectedId);
  const typeById = (id) => types.find((t) => t.id === id);
  const visibleContacts = typeFilter ? contacts.filter((c) => c.contact_type_id === typeFilter) : contacts;

  return (
    <>
      <div className="page-toolbar" data-testid="contacts-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Contacts</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        <input className="sel-input list-search" style={{ width: 200 }} placeholder="⌕ Search name, email, phone, company"
               value={search} onChange={(e) => setSearch(e.target.value)} data-testid="contact-search" />
        <select className="sel-input" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="stage-filter">
          <option value="">All stages</option>
          <StageOptions stages={lob.pipelineStages} />
        </select>
        <select className="sel-input" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="type-filter">
          <option value="">All types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
        </select>
        <button className="dp-btn" onClick={() => setShowTypes(true)} data-testid="manage-types-btn">
          <Tag size={10} style={{ marginRight: 4 }} />Manage types
        </button>
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="add-contact-btn">
          <Plus size={11} style={{ marginRight: 4 }} />Add contact
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="contact-error">{err}</div>}

      <div className="split-pane">
        <div className="list-pane">
          <div className="list-scroll" data-testid="contact-list">
            {loading && contacts.length === 0 && <div className="empty-state">Loading…</div>}
            {!loading && visibleContacts.length === 0 && (
              <div className="empty-state" data-testid="contacts-empty">
                <div className="ttl">No contacts</div>
                <div className="sub">Click <b>Add contact</b> to create your first.</div>
              </div>
            )}
            {visibleContacts.map((c) => {
              const ct = typeById(c.contact_type_id);
              return (
              <div key={c.id} className={`contact-list-row${selectedId === c.id ? ' selected' : ''}`}
                   onClick={() => setSelectedId(c.id)} data-testid={`contact-row-${c.id}`}>
                <div className="init-circle" style={{ background: aBg, color: aTc }}>{(c.first?.[0]||'').toUpperCase()}{(c.last?.[0]||'').toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                    {c.first} {c.last}
                    {ct && (
                      <span className="tag-pill" style={{ background: `${ct.color}22`, color: ct.color, fontSize: 9 }} data-testid={`contact-type-badge-${c.id}`}>
                        {ct.icon} {ct.name}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-faded)' }}>{c.company || c.email || c.phone}</div>
                </div>
                {c.amount ? <span style={{ fontSize: 11, fontWeight: 600 }}>{fmtMoney(c.amount)}</span> : null}
              </div>
              );
            })}
          </div>
        </div>

        <div className="detail-pane" data-testid="contact-detail-pane">
          {!selected ? (
            <div className="empty-detail">
              <div style={{ fontSize: 32, opacity: 0.2 }}>👤</div>
              <div>Select a contact to view details</div>
            </div>
          ) : (
            <ContactDetail contact={selected} lob={lob} types={types} aBg={aBg} aTc={aTc} onChange={load} onDeleted={() => { setSelectedId(null); load(); }} />
          )}
        </div>
      </div>

      {showCreate && (
        <ContactModal lob={lob} types={types} onClose={(saved) => { setShowCreate(false); if (saved) load(); }} />
      )}
      {showTypes && (
        <ContactTypesManager lob={lob} onClose={() => { setShowTypes(false); loadTypes(); }} />
      )}
    </>
  );
};

const ContactDetail = ({ contact, lob, types, aBg, aTc, onChange, onDeleted }) => {
  const [tab, setTab] = useState('details');
  const [showEdit, setShowEdit] = useState(false);
  const ct = types.find((t) => t.id === contact.contact_type_id);

  const del = async () => {
    if (!window.confirm(`Delete ${contact.first} ${contact.last}?`)) return;
    try { await api.delete(`/contacts/${contact.id}`); onDeleted(); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <>
      <div className="dp-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div className="init-circle" style={{ width: 42, height: 42, fontSize: 14, background: aBg, color: aTc }}>
            {contact.first?.[0]?.toUpperCase()}{contact.last?.[0]?.toUpperCase()}
          </div>
          <div>
            <div className="dp-title" style={{ marginBottom: 2 }}>{contact.first} {contact.last}</div>
            <div style={{ fontSize: 11, color: 'var(--text-faded)' }}>{contact.company || '—'}</div>
            <div style={{ marginTop: 4, display: 'flex', gap: 6 }}>
              {contact.stage && <span className="tag-pill muted">{contact.stage}</span>}
              {ct && <span className="tag-pill" style={{ background: `${ct.color}22`, color: ct.color }} data-testid="contact-detail-type-badge">{ct.icon} {ct.name}</span>}
            </div>
          </div>
        </div>
        <div className="dp-actions">
          <button className={`dp-btn${tab === 'email' ? ' primary' : ''}`} onClick={() => setTab('email')} data-testid="contact-email-tab"><Mail size={10} style={{ marginRight: 4 }} />Email</button>
          <button className={`dp-btn${tab === 'sms' ? ' primary' : ''}`} onClick={() => setTab('sms')} data-testid="contact-sms-tab"><MessageSquare size={10} style={{ marginRight: 4 }} />SMS</button>
          <button className={`dp-btn${tab === 'tasks' ? ' primary' : ''}`} onClick={() => setTab('tasks')} data-testid="contact-tasks-tab"><CheckSquare size={10} style={{ marginRight: 4 }} />Tasks</button>
          <button className="dp-btn" data-testid="contact-call-btn"><Phone size={10} style={{ marginRight: 4 }} />Call</button>
          <button className="dp-btn" onClick={() => setShowEdit(true)} data-testid="contact-edit-btn">Edit</button>
          <button className="dp-btn" onClick={del} data-testid="contact-delete-btn"><Trash2 size={10} style={{ marginRight: 4 }} />Delete</button>
        </div>
      </div>
      <div className="dp-content">
        {tab === 'details' && <ContactDetailsCard contact={contact} contactType={ct} />}
        {tab === 'email' && <EmailPanel contact={contact} />}
        {tab === 'sms' && <SmsPanel contact={contact} />}
        {tab === 'tasks' && <ContactTasksPanel contact={contact} />}
      </div>

      {showEdit && (
        <ContactModal lob={lob} contact={contact} types={types} onClose={(saved) => { setShowEdit(false); if (saved) onChange(); }} />
      )}
    </>
  );
};

const ContactDetailsCard = ({ contact, contactType }) => (
  <>
    <div className="card">
      <div className="card-hd">Contact details</div>
      <div className="field-row"><span className="fl">Email</span><span className="fv" style={{ color: 'var(--text-active)' }}>{contact.email || '—'}</span></div>
      <div className="field-row"><span className="fl">Phone</span><span className="fv">{contact.phone || '—'}</span></div>
      <div className="field-row"><span className="fl">Title</span><span className="fv">{contact.title || '—'}</span></div>
      <div className="field-row"><span className="fl">Company</span><span className="fv">{contact.company || '—'}</span></div>
    </div>
    <div className="card">
      <div className="card-hd">Deal context</div>
      <div className="field-row"><span className="fl">Stage</span><span className="fv">{contact.stage || '—'}</span></div>
      <div className="field-row"><span className="fl">Amount</span><span className="fv">{contact.amount ? fmtMoney(contact.amount) : '—'}</span></div>
      <div className="field-row"><span className="fl">Notes</span><span className="fv" style={{ whiteSpace: 'pre-wrap' }}>{contact.notes || '—'}</span></div>
    </div>
    {contactType && (
      <div className="card" data-testid="contact-type-fields-card">
        <div className="card-hd">{contactType.icon} {contactType.name} details</div>
        {(contactType.fields || []).length === 0 && <div style={{ fontSize: 11, color: 'var(--text-faded)', padding: '4px 0' }}>No custom fields on this type.</div>}
        {(contactType.fields || []).map((f) => (
          <div className="field-row" key={f.id}>
            <span className="fl">{f.label}</span>
            <span className="fv">{contact.custom_fields?.[f.id] != null && contact.custom_fields?.[f.id] !== '' ? String(contact.custom_fields[f.id]) : '—'}</span>
          </div>
        ))}
      </div>
    )}
  </>
);

const EmailPanel = ({ contact }) => (
  <div className="card">
    <div className="card-hd">Email <span className="card-sub">{contact.email || 'No email on file'}</span></div>
    <p style={{ fontSize: 11, color: 'var(--text-faded)', padding: 10, lineHeight: 1.5 }}>
      Email send is wired into the Marketing module. Use that to launch campaigns and broadcasts. Direct
      one-off emails will be added when an SMTP integration (Resend / SendGrid) is configured.
    </p>
  </div>
);

const SmsPanel = ({ contact }) => {
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [config, setConfig] = useState(null);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    api.get(`/sms/messages?contact_id=${contact.id}`).then((r) => setMessages(r.data)).catch(() => {});
    api.get('/sms/config').then((r) => setConfig(r.data)).catch(() => {});
  }, [contact.id]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true); setErr('');
    try {
      await api.post('/sms/send', { contact_id: contact.id, body });
      setBody(''); load();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setSending(false); }
  };

  return (
    <div className="card" data-testid="sms-panel">
      <div className="card-hd">SMS <span className="card-sub">{contact.phone || 'No phone'}</span></div>
      {!contact.phone && <div className="module-banner err">Add a phone number to send SMS.</div>}
      {config && !config.configured && (
        <div className="module-banner err" data-testid="sms-not-configured">
          SMS is not configured. Add Twilio credentials in <code>backend/.env</code>.
        </div>
      )}
      <div className="sms-thread" data-testid="sms-thread">
        {messages.length === 0 && <div style={{ padding: 10, fontSize: 11, color: 'var(--text-faded)' }}>No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.id} className={`sms-msg ${m.direction === 'outbound' ? 'out' : 'in'}`} data-testid={`sms-msg-${m.id}`}>
            <div>{m.body}</div>
            <div className="sms-meta">{m.status} · {new Date(m.sent_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
      {err && <div className="f-error">{err}</div>}
      <form className="sms-input-row" onSubmit={send}>
        <textarea
          rows={2} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={contact.phone ? `SMS to ${contact.phone}` : 'No phone on file'}
          disabled={!contact.phone}
          data-testid="sms-body-input"
        />
        <button type="submit" className="btn-primary" disabled={!body.trim() || !contact.phone || sending} data-testid="sms-send-btn">
          <Send size={10} style={{ marginRight: 4 }} />{sending ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
};

const ContactTasksPanel = ({ contact }) => {
  const [tasks, setTasks] = useState([]);
  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    api.get(`/tasks?contact_id=${contact.id}`).then((r) => setTasks(r.data)).catch(() => {});
  }, [contact.id]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true); setErr('');
    try {
      await api.post('/tasks', { contact_id: contact.id, title, due_date: dueDate || null, priority });
      setTitle(''); setDueDate(''); setPriority('medium'); load();
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  const toggle = async (t) => {
    try { await api.patch(`/tasks/${t.id}`, { status: t.status === 'completed' ? 'open' : 'completed' }); load(); }
    catch (ex) { setErr(formatApiError(ex)); }
  };

  const del = async (id) => {
    try { await api.delete(`/tasks/${id}`); load(); }
    catch (ex) { setErr(formatApiError(ex)); }
  };

  return (
    <div className="card" data-testid="contact-tasks-panel">
      <div className="card-hd">Tasks <span className="card-sub">{tasks.filter((t) => t.status !== 'completed').length} open</span></div>
      {tasks.length === 0 && <div style={{ padding: 10, fontSize: 11, color: 'var(--text-faded)' }}>No tasks yet — add one below.</div>}
      {tasks.map((t) => (
        <div key={t.id} className="action-row" data-testid={`contact-task-row-${t.id}`}>
          <input type="checkbox" checked={t.status === 'completed'} onChange={() => toggle(t)} data-testid={`contact-task-check-${t.id}`} />
          <div style={{ flex: 1, minWidth: 0, marginLeft: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, textDecoration: t.status === 'completed' ? 'line-through' : 'none', opacity: t.status === 'completed' ? 0.5 : 1 }}>{t.title}</div>
            <div style={{ fontSize: 10, color: 'var(--text-faded)' }}>{t.due_date ? `Due ${t.due_date}` : 'No due date'} · {t.priority}</div>
          </div>
          <button className="btn-link" style={{ color: 'var(--text-faded)', padding: 0 }} onClick={() => del(t.id)} data-testid={`contact-task-delete-${t.id}`}><Trash2 size={11} /></button>
        </div>
      ))}
      {err && <div className="f-error">{err}</div>}
      <form onSubmit={add} style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input className="f-input" style={{ flex: '1 1 160px' }} placeholder="New task…" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="contact-task-title-input" />
        <input className="f-input" type="date" style={{ width: 130 }} value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="contact-task-due-input" />
        <select className="f-select" style={{ width: 100 }} value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="contact-task-priority-input">
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
        <button type="submit" className="btn-primary" disabled={busy || !title.trim()} data-testid="contact-task-add-btn">{busy ? 'Adding…' : 'Add'}</button>
      </form>
    </div>
  );
};

const CustomFieldInput = ({ field, value, onChange }) => {
  const common = { 'data-testid': `cm-custom-${field.id}` };
  if (field.type === 'textarea') {
    return <textarea className="f-textarea" rows={2} value={value || ''} onChange={(e) => onChange(e.target.value)} {...common} />;
  }
  if (field.type === 'select') {
    return (
      <select className="f-select" value={value || ''} onChange={(e) => onChange(e.target.value)} {...common}>
        <option value="">—</option>
        {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === 'checkbox') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} {...common} />
        {field.placeholder || 'Yes'}
      </label>
    );
  }
  return (
    <input
      className="f-input"
      type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'email' ? 'email' : 'text'}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      {...common}
    />
  );
};

const ContactModal = ({ lob, contact, types, onClose }) => {
  const [form, setForm] = useState(contact || {
    lob_id: lob.id, first: '', last: '', email: '', phone: '', company: '', title: '',
    stage: '', amount: '', notes: '', contact_type_id: '', custom_fields: {},
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (fieldId, v) => setForm((f) => ({ ...f, custom_fields: { ...(f.custom_fields || {}), [fieldId]: v } }));
  const activeType = types.find((t) => t.id === form.contact_type_id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    const payload = { ...form, amount: form.amount === '' ? null : Number(form.amount), contact_type_id: form.contact_type_id || null };
    try {
      if (contact) await api.patch(`/contacts/${contact.id}`, payload);
      else await api.post('/contacts', payload);
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="contact-modal">
        <div className="modal-title">{contact ? 'Edit contact' : 'Add contact'}</div>
        <div className="modal-row">
          <div><label className="f-label">First name</label><input className="f-input" value={form.first} onChange={(e) => set('first', e.target.value)} required data-testid="cm-first" /></div>
          <div><label className="f-label">Last name</label><input className="f-input" value={form.last} onChange={(e) => set('last', e.target.value)} required data-testid="cm-last" /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Email</label><input className="f-input" type="email" value={form.email || ''} onChange={(e) => set('email', e.target.value)} data-testid="cm-email" /></div>
          <div><label className="f-label">Phone (E.164 e.g. +14155550123)</label><input className="f-input" value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} data-testid="cm-phone" /></div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Company</label><input className="f-input" value={form.company || ''} onChange={(e) => set('company', e.target.value)} data-testid="cm-company" /></div>
          <div><label className="f-label">Title</label><input className="f-input" value={form.title || ''} onChange={(e) => set('title', e.target.value)} data-testid="cm-title" /></div>
        </div>
        <div className="modal-row">
          <div>
            <label className="f-label">Stage</label>
            <select className="f-select" value={form.stage || ''} onChange={(e) => set('stage', e.target.value)} data-testid="cm-stage">
              <option value="">—</option>
              <StageOptions stages={lob.pipelineStages} />
            </select>
          </div>
          <div><label className="f-label">Deal amount ($)</label><input className="f-input" type="number" step="0.01" value={form.amount ?? ''} onChange={(e) => set('amount', e.target.value)} data-testid="cm-amount" /></div>
        </div>
        <label className="f-label">Type</label>
        <select className="f-select" value={form.contact_type_id || ''} onChange={(e) => set('contact_type_id', e.target.value)} data-testid="cm-type">
          <option value="">— No type —</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.icon} {t.name}</option>)}
        </select>
        {activeType && (activeType.fields || []).length > 0 && (
          <div style={{ marginTop: 10, marginBottom: 4, padding: 10, borderRadius: 8, background: 'var(--surface-2)' }} data-testid="cm-custom-fields">
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-faded)', marginBottom: 8 }}>
              {activeType.icon} {activeType.name} details
            </div>
            {activeType.fields.map((f) => (
              <div key={f.id} style={{ marginBottom: 8 }}>
                <label className="f-label">{f.label}{f.required && <span className="req"> *</span>}</label>
                <CustomFieldInput field={f} value={form.custom_fields?.[f.id]} onChange={(v) => setCustom(f.id, v)} />
              </div>
            ))}
          </div>
        )}
        <label className="f-label">Notes</label>
        <textarea className="f-textarea" rows={3} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} data-testid="cm-notes" />
        {err && <div className="f-error" data-testid="cm-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)} data-testid="cm-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="cm-submit">{busy ? 'Saving…' : (contact ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

const ContactTypesManager = ({ lob, onClose }) => {
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/contact-types?lob_id=${lob.id}`)
      .then((r) => { setItems(r.data); setSelectedId((cur) => cur || (r.data[0] && r.data[0].id) || null); })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [lob.id]);

  useEffect(() => { load(); }, [load]);

  const selected = items.find((t) => t.id === selectedId);

  const createNew = async () => {
    try {
      const { data } = await api.post('/contact-types', { lob_id: lob.id, name: 'New Type', icon: '🏷️', color: '#3368b8', fields: [] });
      setItems((s) => [...s, data]);
      setSelectedId(data.id);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const saveType = async (draft) => {
    try {
      const { data } = await api.patch(`/contact-types/${draft.id}`, { name: draft.name, icon: draft.icon, color: draft.color, fields: draft.fields });
      setItems((s) => s.map((t) => (t.id === data.id ? data : t)));
    } catch (e) { setErr(formatApiError(e)); }
  };

  const deleteType = async (id) => {
    if (!window.confirm('Delete this type? Contacts using it will keep their info but lose the type label.')) return;
    try {
      await api.delete(`/contact-types/${id}`);
      setItems((s) => s.filter((t) => t.id !== id));
      setSelectedId((cur) => (cur === id ? null : cur));
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div className="modal-overlay-v2" onClick={onClose}>
      <div className="modal-card" style={{ maxWidth: 820, width: '92vw' }} onClick={(e) => e.stopPropagation()} data-testid="contact-types-modal">
        <div className="modal-title">Manage contact types — {lob.label}</div>
        <div style={{ display: 'flex', gap: 16, minHeight: 380 }}>
          <div style={{ width: 190, flexShrink: 0, borderRight: '1px solid var(--stroke-light)', paddingRight: 12, overflowY: 'auto' }}>
            {loading && <div style={{ fontSize: 11, color: 'var(--text-faded)' }}>Loading…</div>}
            {items.map((t) => (
              <div
                key={t.id}
                onClick={() => setSelectedId(t.id)}
                data-testid={`contact-type-row-${t.id}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                  fontSize: 12, marginBottom: 3,
                  background: selectedId === t.id ? 'var(--surface-2)' : 'transparent',
                  fontWeight: selectedId === t.id ? 600 : 400,
                }}
              >
                <span>{t.icon}</span><span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
              </div>
            ))}
            <button type="button" className="dp-btn" style={{ width: '100%', marginTop: 8, justifyContent: 'center' }} onClick={createNew} data-testid="add-contact-type-btn">
              <Plus size={11} style={{ marginRight: 4 }} />New type
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 460 }}>
            {!selected ? (
              <div className="empty-state"><div className="ttl">No type selected</div><div className="sub">Create a type or pick one from the list.</div></div>
            ) : (
              <ContactTypeEditor key={selected.id} initial={selected} onSave={saveType} onDelete={() => deleteType(selected.id)} />
            )}
          </div>
        </div>
        {err && <div className="f-error">{err}</div>}
        <div className="modal-actions"><button type="button" className="btn-secondary" onClick={onClose} data-testid="contact-types-close-btn">Close</button></div>
      </div>
    </div>
  );
};

const ContactTypeEditor = ({ initial, onSave, onDelete }) => {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  useEffect(() => { setDraft(initial); }, [initial]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const addField = () => setDraft((d) => ({ ...d, fields: [...(d.fields || []), { id: `new-${Date.now()}`, type: 'text', label: 'New field', required: false, options: [] }] }));
  const updateField = (idx, patch) => setDraft((d) => ({ ...d, fields: d.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)) }));
  const removeField = (idx) => setDraft((d) => ({ ...d, fields: d.fields.filter((_, i) => i !== idx) }));
  const moveField = (idx, dir) => setDraft((d) => {
    const fields = [...d.fields];
    const j = idx + dir;
    if (j < 0 || j >= fields.length) return d;
    [fields[idx], fields[j]] = [fields[j], fields[idx]];
    return { ...d, fields };
  });

  const save = async () => {
    setBusy(true);
    await onSave(draft);
    setBusy(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  return (
    <div data-testid="contact-type-editor">
      <div className="modal-row">
        <div>
          <label className="f-label">Icon (emoji)</label>
          <input className="f-input" value={draft.icon} onChange={(e) => set('icon', e.target.value)} style={{ width: 60 }} data-testid="ct-icon-input" />
        </div>
        <div>
          <label className="f-label">Name</label>
          <input className="f-input" value={draft.name} onChange={(e) => set('name', e.target.value)} data-testid="ct-name-input" />
        </div>
        <div>
          <label className="f-label">Badge color</label>
          <input type="color" value={draft.color} onChange={(e) => set('color', e.target.value)} style={{ width: 60, height: 30, padding: 2 }} data-testid="ct-color-input" />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, marginBottom: 6 }}>
        <label className="f-label" style={{ margin: 0 }}>Custom fields</label>
        <button type="button" className="dp-btn" onClick={addField} data-testid="ct-add-field-btn"><Plus size={10} style={{ marginRight: 4 }} />Add field</button>
      </div>

      {(draft.fields || []).length === 0 && <div style={{ fontSize: 11, color: 'var(--text-faded)', padding: '8px 0' }}>No custom fields yet — add one above.</div>}

      {(draft.fields || []).map((f, idx) => (
        <div key={f.id} className="card" style={{ marginBottom: 8, padding: 10 }} data-testid={`ct-field-row-${idx}`}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <GripVertical size={12} style={{ opacity: 0.4, flexShrink: 0 }} />
            <input className="f-input" style={{ flex: 1.4 }} value={f.label} onChange={(e) => updateField(idx, { label: e.target.value })} placeholder="Field label" data-testid={`ct-field-label-${idx}`} />
            <select className="f-select" style={{ flex: 1 }} value={f.type} onChange={(e) => updateField(idx, { type: e.target.value })} data-testid={`ct-field-type-${idx}`}>
              {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <label style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={!!f.required} onChange={(e) => updateField(idx, { required: e.target.checked })} data-testid={`ct-field-required-${idx}`} />Req.
            </label>
            <button type="button" className="dp-btn" style={{ padding: '4px 7px' }} onClick={() => moveField(idx, -1)} disabled={idx === 0} data-testid={`ct-field-up-${idx}`}>↑</button>
            <button type="button" className="dp-btn" style={{ padding: '4px 7px' }} onClick={() => moveField(idx, 1)} disabled={idx === draft.fields.length - 1} data-testid={`ct-field-down-${idx}`}>↓</button>
            <button type="button" className="dp-btn" style={{ padding: '4px 7px' }} onClick={() => removeField(idx)} data-testid={`ct-field-remove-${idx}`}><X size={12} /></button>
          </div>
          {f.type === 'select' && (
            <input
              className="f-input" style={{ marginTop: 6 }}
              placeholder="Options, comma-separated"
              value={(f.options || []).join(', ')}
              onChange={(e) => updateField(idx, { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })}
              data-testid={`ct-field-options-${idx}`}
            />
          )}
        </div>
      ))}

      <div className="modal-actions" style={{ marginTop: 16 }}>
        <button type="button" className="dp-btn" onClick={onDelete} data-testid="ct-delete-btn"><Trash2 size={10} style={{ marginRight: 4 }} />Delete type</button>
        <button type="button" className="btn-primary" onClick={save} disabled={busy} data-testid="ct-save-btn">{busy ? 'Saving…' : savedFlash ? 'Saved ✓' : 'Save type'}</button>
      </div>
    </div>
  );
};

export default Contacts;
